import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, phpFmt } from "../lib/api";
import ProtectedRoute from "../components/ProtectedRoute";
import { Plus, Clock, CheckCircle2, XCircle, MapPin } from "lucide-react";

const StatusPill = ({ s }) => {
    const map = {
        pending: { t: "Pending review", cls: "bg-yellow-100 text-yellow-800", icon: Clock },
        approved: { t: "Live", cls: "bg-green-100 text-green-800", icon: CheckCircle2 },
        rejected: { t: "Rejected", cls: "bg-red-100 text-red-800", icon: XCircle },
    };
    const { t, cls, icon: I } = map[s] || map.pending;
    return (
        <span className={`text-[10px] uppercase tracking-label px-2 py-1 inline-flex items-center gap-1 ${cls}`}>
            <I className="h-3 w-3" strokeWidth={1.5} /> {t}
        </span>
    );
};

function Body() {
    const [centers, setCenters] = useState([]);
    const [loading, setLoading] = useState(true);
    useEffect(() => {
        api.get("/provider/centers").then((r) => setCenters(r.data)).finally(() => setLoading(false));
    }, []);

    return (
        <div className="pt-24 min-h-screen">
            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-12">
                <div className="flex items-end justify-between flex-wrap gap-4">
                    <div>
                        <div className="text-[10px] uppercase tracking-label text-zinc-500">Provider Dashboard</div>
                        <h1 className="font-display text-5xl font-semibold tracking-tighter mt-2">My Centers</h1>
                    </div>
                    <Link data-testid="enroll-new-center-link" to="/enroll" className="inline-flex items-center gap-2 bg-[#FF6A13] text-white px-5 py-3 text-sm hover:bg-orange-600 transition-colors">
                        <Plus className="h-4 w-4" strokeWidth={1.5} /> Enroll a new center
                    </Link>
                </div>

                <div className="mt-10">
                    {loading ? (
                        <div className="text-zinc-500 text-sm tracking-label uppercase">Loading…</div>
                    ) : centers.length === 0 ? (
                        <div data-testid="empty-centers" className="border border-zinc-200 bg-zinc-50 p-12 text-center">
                            <div className="font-display text-2xl font-semibold">No centers yet.</div>
                            <p className="mt-2 text-sm text-zinc-600">Enroll your first center and reach a curated audience of businessmen.</p>
                            <Link to="/enroll" className="mt-6 inline-flex items-center gap-2 bg-zinc-950 text-white px-5 py-3 text-sm hover:bg-[#FF6A13] transition-colors">
                                <Plus className="h-4 w-4" strokeWidth={1.5} /> Enroll a center
                            </Link>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-zinc-200 border border-zinc-200">
                            {centers.map((c) => (
                                <div key={c.center_id} data-testid={`provider-center-${c.center_id}`} className="bg-white p-5 flex flex-col">
                                    <div className="aspect-[4/3] bg-zinc-100 overflow-hidden">
                                        <img src={c.photos?.[0] || "https://images.pexels.com/photos/7651627/pexels-photo-7651627.jpeg"} alt="" className="h-full w-full object-cover" />
                                    </div>
                                    <div className="mt-4 flex items-start justify-between gap-2">
                                        <div>
                                            <div className="text-[10px] uppercase tracking-label text-zinc-500 flex items-center gap-1.5">
                                                <MapPin className="h-3 w-3" strokeWidth={1.5} /> {c.city}
                                            </div>
                                            <div className="font-display text-xl font-semibold tracking-tight mt-1">{c.name}</div>
                                        </div>
                                        <StatusPill s={c.status} />
                                    </div>
                                    <div className="mt-2 text-xs text-zinc-500 line-clamp-2">{c.public_area}</div>
                                    <div className="mt-3 font-display text-lg font-semibold">{phpFmt(c.monthly_rate_php)} <span className="text-[10px] text-zinc-500 uppercase tracking-label">/ mo</span></div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default function ProviderDashboard() {
    return (
        <ProtectedRoute roles={["provider", "admin"]}>
            <Body />
        </ProtectedRoute>
    );
}
